import { gql } from '@common/hooks';
import {
  WorksListItemExpirationType,
  WorksListItemLongTypesType,
} from '@onboarding/services';
import {
  DetailItemAttachments,
  DetailItemAttachmentType,
} from '@onboarding/graphql/fragments';

export type NurseProfileDocumentType = {
  id: string;
  title: string;
  docType: string;
};

export type NurseProfileLicenseType = {
  id: string;
  state: string;
  friendlyLicenseType: string;
  number: string;
};

export type NurseProfileHealthDocsType = {
  id: string;
  healthDoc: {
    id: string;
    name: string;
  };
};

export type NurseProfileCertificationType = {
  id: string;
  name: string;
  issuingBody: string;
};

export type NurseProfileAssessmentType = {
  id: string;
  name: string;
  assessment: {
    id: string;
    name: string;
  };
};

export type NurseProfileIdDocsType = {
  id: string;
  title: string;
  docType: string;
  docSubtype: string;
};

export type WorksProfileType = {
  id: string;
  licenses: NurseProfileLicenseType[];
  nurseCertifications: NurseProfileCertificationType[];
  nurseAssessments: NurseProfileAssessmentType[];
  nurseHealthDocs: NurseProfileHealthDocsType[];
  documents: NurseProfileDocumentType[];
  idDocs: NurseProfileIdDocsType[];
};

type AssessmentIssuerType = {
  id: string;
  name: string;
};

export type ComplianceItemType = {
  id: string;
  compliant: boolean;
  compliantForAssignment: boolean;
  expirationType: WorksListItemExpirationType;
  expiresOn: string;
  name: string;
  state: string;
  type: WorksListItemLongTypesType;
  typeName: string;
  stateApprovedAt: string;
  canDeleteAttachment: boolean;

  detailItem?: {
    id: string;
    title: string;
    docType: string; // Enum?
    expiresOn: string;
    isExpired?: boolean;
    isExpiringSoon?: boolean;
    completedOn: string;
    name?: string;
    detailItemAttachments?: DetailItemAttachmentType[];

    // Certification
    certificationUuid?: string;

    // License
    stateCode?: string;
    number?: string;
    compact?: boolean;
    licenseType?: string;
  };

  // Assessment
  assessment?: {
    id: string;
    name: string;
    category: string;
  };
  assessmentIssuers?: AssessmentIssuerType[];
  assessmentIssuersToBeSelected?: AssessmentIssuerType[];
  issuedAssessment: {
    id: string;
    assessment: {
      id: string;
      name: string;
      category: string;
    };
    issuer: {
      id: string;
      name: string;
    };
  };

  // Certification
  certification?: {
    id: string;
    name: string;
    credential: string;
    issuingBodies: string[];
    useCompletionDate: boolean;
  };

  // ID Doc
  documentTemplate?: {
    id: string;
    key: string;
    name: string;
  };
  documentTemplateSubtype?: string;

  // healthDoc
  healthDoc?: {
    id: string;
    name: string;
  };

  worksList: {
    id: string;
    staffingRequestAssignment: {
      id: string;
      worksProfile: WorksProfileType;
    };
  };
};

export type ComplianceItemUpsertDrawerQueryType = {
  worksListItem: ComplianceItemType;
};

export const ComplianceItemUpsertDrawerQuery = gql`
  query ComplianceItemUpsertDrawerQuery($id: ID!) {
    worksListItem(id: $id) {
      id
      compliant
      compliantForAssignment
      expirationType
      expiresOn
      name
      state
      type
      typeName
      stateApprovedAt
      canDeleteAttachment

      ... on AssessmentItem {
        id
        name
        issuedAssessment {
          id
          assessment {
            id
            name
            category
          }
          issuer {
            id
            name
          }
        }
        assessmentIssuers {
          id
          name
        }
        assessmentIssuersToBeSelected {
          id
          name
        }
        assessment {
          id
          name
          category
        }
      }

      ... on BackgroundIdentificationItem {
        documentTemplate {
          id
          name
          key
        }
        documentTemplateSubtype
      }

      ... on ImmigrationItem {
        documentTemplate {
          id
          name
          key
        }
        documentTemplateSubtype
      }

      ... on CertificationItem {
        certification {
          id
          name
          issuingBodies
          credential
          useCompletionDate
        }
      }

      ... on HealthDocumentItem {
        healthDocSubtype
        healthDoc {
          id
          name
        }
      }

      ... on LicenseItem {
        licenseType
      }

      detailItem {
        ... on License {
          id
          expiresOn
          completedOn
          stateCode
          number
          compact
          licenseType
          isExpired
          isExpiringSoon
          detailItemAttachments {
            ...DetailItemAttachments
          }
        }
        ... on NurseAssessment {
          id
          expiresOn
          completedOn
          isExpired
          isExpiringSoon
          detailItemAttachments {
            ...DetailItemAttachments
          }
        }
        ... on NurseHealthDoc {
          id
          expiresOn
          completedOn
          isExpired
          isExpiringSoon
          healthDoc {
            id
            name
          }
          detailItemAttachments {
            ...DetailItemAttachments
          }
        }
        ... on NurseCertification {
          id
          name
          useCompletionDate
          completedOn
          expiresOn
          issuingBody
          certificationUuid
          isExpired
          isExpiringSoon
          detailItemAttachments {
            ...DetailItemAttachments
          }
        }
        ... on Document {
          id
          title
          expiresOn
          completedOn
          docType
          isExpired
          isExpiringSoon
          detailItemAttachments {
            ...DetailItemAttachments
          }
        }
      }
      worksList {
        id
        staffingRequestAssignment {
          id
          worksProfile {
            id
            licenses {
              id
              state
              friendlyLicenseType
              number
              expiresOn
            }
            nurseCertifications {
              id
              certificationUuid
              name
              issuingBody
            }

            nurseAssessments {
              id
              completedOn

              assessment {
                id
                name
              }
            }

            nurseHealthDocs {
              id
              healthDoc {
                id
                name
                categories(filter: PROVIDER)
              }
              completedOn
              category
            }

            documents {
              id
              title
              docType
            }

            idDocs {
              id
              title
              docType
              docSubtype
            }
          }
        }
      }
    }
  }
  ${DetailItemAttachments}
`;
